import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Buildings, ShieldCheck, Lightning } from "@phosphor-icons/react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regLoading, setRegLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await login(loginEmail.trim().toLowerCase(), loginPassword);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    try {
      await register({
        name: regName.trim(),
        email: regEmail.trim().toLowerCase(),
        password: regPassword,
        role: "purchase_manager",
      });
      toast.success("Account created");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRegLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left – branding panel */}
      <div
        className="hidden lg:flex relative flex-col justify-between p-12 text-white overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(rgba(3,7,18,0.78), rgba(3,7,18,0.92)), url(https://static.prod-images.emergentagent.com/jobs/8edb9b1d-d169-4c51-9caf-4228cc8b337f/images/26cd0e00c968df110996ce4e229462a238eea7a028af10bc063d2d71f49affaf.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-primary flex items-center justify-center">
            <Buildings size={22} weight="bold" />
          </div>
          <div>
            <div className="font-display text-xl font-bold leading-tight">RRL PO Management</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">Powered by marketly.tech</div>
          </div>
        </div>

        <div className="max-w-lg space-y-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/20 text-[11px] uppercase tracking-[0.18em]">
            <span className="h-1.5 w-1.5 bg-primary rounded-full animate-pulse" />
            Live Control Room
          </div>
          <h1 className="font-display text-5xl font-bold leading-[0.95]">
            Raise. Validate.<br />
            <span className="text-primary">Reconcile.</span>
          </h1>
          <p className="text-white/70 leading-relaxed text-base max-w-md">
            A structural tool for construction procurement — built to match every tax invoice against the purchase
            order you raised, down to the quantity and price.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-6 relative z-10 max-w-lg">
          <div className="border-l-2 border-primary pl-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/60 mb-1">
              <ShieldCheck size={14} /> Auto-Validated
            </div>
            <div className="font-display text-2xl font-bold">Qty ≤ PO</div>
          </div>
          <div className="border-l-2 border-primary pl-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/60 mb-1">
              <Lightning size={14} /> AI-Extracted
            </div>
            <div className="font-display text-2xl font-bold">Price · Match</div>
          </div>
        </div>
      </div>

      {/* Right – form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-9 w-9 bg-primary flex items-center justify-center">
              <Buildings size={18} weight="bold" color="white" />
            </div>
            <div>
              <div className="font-display text-lg font-bold leading-tight">RRL PO Management</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">Powered by marketly.tech</div>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">Sign In</div>
            <h2 className="font-display text-4xl font-bold tracking-tight">Enter the control room</h2>
            <p className="text-sm text-neutral-500 mt-2">Manage purchase orders and validate tax invoices.</p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-sm">
              <TabsTrigger value="login" className="rounded-sm" data-testid="tab-login">Login</TabsTrigger>
              <TabsTrigger value="register" className="rounded-sm" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email" className="text-xs uppercase tracking-wider text-neutral-600">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    required
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="rounded-sm h-11"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password" className="text-xs uppercase tracking-wider text-neutral-600">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    required
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="rounded-sm h-11"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full h-11 rounded-sm bg-primary hover:bg-[#0039CC] text-white font-medium"
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? "Signing in…" : "Sign In →"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-name" className="text-xs uppercase tracking-wider text-neutral-600">Full Name</Label>
                  <Input id="reg-name" required value={regName} onChange={(e) => setRegName(e.target.value)} className="rounded-sm h-11" data-testid="register-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-email" className="text-xs uppercase tracking-wider text-neutral-600">Email</Label>
                  <Input id="reg-email" type="email" required value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className="rounded-sm h-11" data-testid="register-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-password" className="text-xs uppercase tracking-wider text-neutral-600">Password</Label>
                  <Input id="reg-password" type="password" required minLength={6} value={regPassword} onChange={(e) => setRegPassword(e.target.value)} className="rounded-sm h-11" data-testid="register-password-input" />
                </div>
                <Button type="submit" disabled={regLoading} className="w-full h-11 rounded-sm bg-primary hover:bg-[#0039CC] text-white font-medium" data-testid="register-submit-btn">
                  {regLoading ? "Creating account…" : "Create Account →"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
